import { inject, Injectable } from '@angular/core';

import { Store } from '@ngxs/store';
import {
	catchError,
	concat,
	filter,
	first,
	map,
	merge,
	Observable,
	shareReplay,
	Subject,
	switchMap,
	take,
	takeUntil,
	takeWhile,
	throwError,
	timeout,
} from 'rxjs';

import { RebasedWebSocketMessage, RebasedWebSocketMessageType } from '@trovata/app/core/models/rebased-web-socket.model';
import { RebasedWebSocketService } from '@trovata/app/core/services/rebased-web-socket.service';
import { AuthState } from '@trovata/app/core/store/state/auth/auth.state';
import { AgentsAiProvider } from '@trovata/app/features/ai-agents/services/agents-ai-provider';
import {
	AgentStreamEvent,
	AgentConfigurationTextDeltaMessage,
	TmsAgentChatConfigurationMessage,
	TmsAgentConfigResponseMessage,
	WebSocketConnectionError,
	buildTmsAgentChatConfigurationRequest,
} from '@trovata/app/features/ai-agents/models/agents-websocket.model';
import { AgentsState } from '@trovata/app/features/ai-agents/store/state/agents.state';
import { AgentFormState } from '@trovata/app/features/ai-agents/models/agents.model';

/**
 * TmsAgentsAiProvider — the TMS implementation of {@link AgentsAiProvider}.
 *
 * Drives the agent builder's configuration chat against the TMS-scoped rebased-api
 * WebSocket request route (`ai.tms.agent.chat.configuration.v1`, → the TMS agent's
 * creation mode). The conversational text streams back via the reused Cash
 * `ai.agent.configuration.text.delta` frames, while the terminal signal arrives on
 * the TMS-only typed `ai.tms.agent.config.response` frame, which carries the
 * structured `agentConfig` (instructions/schedule/recipients/description). That typed
 * field rides the `complete` event into `AgentsState`, which auto-fills the builder form
 * (TMS-gated). The PREVIEW is LAZY: `sendPreviewChat` (on Preview-tab click) reuses the
 * same config route with `preview: true` + the current form as `configContext` on the
 * same overlay session — the agent's preview prose streams back as text-delta frames.
 *
 * It uses the SAME shared `RebasedWebSocketService` the Cash
 * `AgentsWebSocketService` uses, and reproduces its request → processing → delta →
 * terminal stream shape. The base `AgentStreamEvent` shape stays identical to the
 * Cash provider's; only the TMS path sets the optional `agentConfig`/`preview` on
 * `complete`, so the shared `AgentsState` handlers consume Cash events unchanged.
 *
 * Cross-stream isolation: Cash and TMS builder streams never coexist (Cash and
 * TMS are mutually-exclusive sibling routes, and this provider is only reachable
 * via `AgentsAiProviderRouter` on a `/tms` URL); the terminal frame is the TMS-only
 * `ai.tms.agent.config.response` type, which the Cash listener never subscribes to.
 *
 * Cash isolation: this provider is only reachable through `AgentsAiProviderRouter`
 * on a `/tms` route, and it neither imports nor mutates `AgentsWebSocketService`.
 * The Cash configurator/preview routes are provably untouched.
 *
 * Spec lock: `documentation/implementation/system/tms-agents-migration.md`
 * §4 (annotation cycle, "Edit-AI is a separate provider seam") + §5 P5.
 */
@Injectable({
	providedIn: 'root',
})
export class TmsAgentsAiProvider implements AgentsAiProvider {
	private readonly store: Store = inject(Store);
	private readonly webSocketService: RebasedWebSocketService = inject(RebasedWebSocketService);

	private readonly abort$: Subject<void> = new Subject<void>();

	sendConfigurationChat(prompt: string, sessionId?: string): Observable<AgentStreamEvent> {
		const authorization: string = this.store.selectSnapshot(AuthState.accessToken);
		// Inject the current form as configContext (mirrors legacy, which sends it on EVERY config turn) so
		// the agent edits in place and preserves unchanged fields. Without it, an edit-mode turn ("change the
		// schedule") sees no existing config → the agent says "no agent configured" and clears Instructions.
		const request: TmsAgentChatConfigurationMessage = buildTmsAgentChatConfigurationRequest(
			prompt,
			sessionId,
			authorization,
			false,
			this.serializeConfigContext()
		);
		return this.waitForConnection().pipe(
			switchMap(() => {
				this.webSocketService.send(request);
				return this.buildConfigStream({ first: 30_000, each: 60_000 });
			})
		);
	}

	/**
	 * Serializes the current builder form as the agent's `configContext` (the current instructions /
	 * schedule / recipients). Sent on both the config and preview turns so the agent always sees the
	 * existing configuration and edits in place — the SSOT for "what the agent currently knows".
	 */
	private serializeConfigContext(): string {
		const form: AgentFormState = this.store.selectSnapshot(AgentsState.configForm);
		return JSON.stringify({
			instructions: form.instructions ?? '',
			schedule: form.triggerConfig ?? {},
			recipients: form.delivery ?? '',
		});
	}

	/**
	 * LAZY preview (matches Cash + legacy): generated ON Preview-tab click, NOT eagerly during the config
	 * turn. Reuses the config route with `preview: true` (one handler, extensible — mirrors legacy
	 * `agent-config` + `preview:true`): sends the current builder form as `configContext` on the SAME
	 * ephemeral overlay session (so the preview is grounded in the conversation), and the agent's preview
	 * prose streams back as text-delta frames → `buildConfigStream` `delta` events → `PreviewAgent`
	 * accumulates them into `previewContent`. The config turn no longer carries a preview, so nothing is
	 * generated up front.
	 *
	 * Reads the live `configForm` + overlay `chatSessionId` from the store (the {@link AgentsAiProvider}
	 * `sendPreviewChat(prompt)` contract carries only the prompt). The lazy-trigger + Preview-tab render
	 * is the shared SSOT (the builder-dialog effect); a future artifacts dual-panel plugs its own
	 * (data-fetch) generation into the same seam.
	 */
	sendPreviewChat(prompt: string, _timezone?: string): Observable<AgentStreamEvent> {
		// `timezone` is accepted for interface parity with the Cash provider but not forwarded: the TMS
		// preview is grounded in `serializeConfigContext()`, whose `schedule` already carries the form's
		// trigger config (and with it the chosen timezone), so passing it separately could disagree with
		// the form the user is looking at.
		const authorization: string = this.store.selectSnapshot(AuthState.accessToken);
		const sessionId: string | undefined = this.store.selectSnapshot(AgentsState.chatSessionId) ?? undefined;
		const request: TmsAgentChatConfigurationMessage = buildTmsAgentChatConfigurationRequest(prompt, sessionId, authorization, true, this.serializeConfigContext());
		return this.waitForConnection().pipe(
			switchMap(() => {
				this.webSocketService.send(request);
				return this.buildConfigStream({ first: 30_000, each: 60_000 });
			})
		);
	}

	/**
	 * Tears down the in-flight configuration-chat run.
	 *
	 * The TMS builder shares ONE WebSocket round-trip between chat and preview (both stream off the
	 * same overlay session), so there is a single teardown signal — unlike the Cash provider, whose
	 * chat and preview are independent runs. Both granular aborts therefore stop the shared stream;
	 * they exist so callers written against the Cash seam behave sensibly here rather than silently
	 * doing nothing.
	 */
	abortChatStream(): void {
		this.abort$.next();
	}

	/** Tears down the in-flight preview run — the same shared stream as `abortChatStream`; see above. */
	abortPreviewStream(): void {
		this.abort$.next();
	}

	abortStream(): void {
		this.abort$.next();
	}

	/**
	 * Connection guard — ensures the WebSocket is ready and connected before sending.
	 * Mirrors `AgentsWebSocketService.waitForConnection`.
	 */
	private waitForConnection(timeoutMs: number = 5_000): Observable<void> {
		return this.webSocketService.ready$.pipe(
			take(1),
			switchMap(() =>
				this.webSocketService.connected$.pipe(
					first((connected: boolean) => connected),
					timeout(timeoutMs),
					map(() => undefined),
					catchError(() => throwError(() => new WebSocketConnectionError()))
				)
			)
		);
	}

	/**
	 * Turns the WebSocket frames for one TMS builder-chat turn into an
	 * `Observable<AgentStreamEvent>`. The conversational text streams via the reused
	 * Cash `ai.agent.configuration.text.delta` frames, while the terminal/processing
	 * signal comes from the TMS-only typed `ai.tms.agent.config.response` frame. On
	 * `status==='success'` the `complete` event carries the typed `agentConfig`/`preview`
	 * (consumed in `agents.state.ts` to auto-fill the builder form + preview tab);
	 * `status==='failed'` → `error`; `status==='processing'` → `processing`.
	 *
	 * Mirrors `AgentsWebSocketService.buildStream`'s `shareReplay`/`concat`/`merge`
	 * shape — see that method for the rationale.
	 */
	private buildConfigStream(timeoutConfig: { first: number; each: number }): Observable<AgentStreamEvent> {
		const lifecycle$: Observable<TmsAgentConfigResponseMessage> = this.webSocketService
			.onMessageType(RebasedWebSocketMessageType.aiTmsAgentConfigResponse)
			.pipe(map((msg: RebasedWebSocketMessage) => msg as TmsAgentConfigResponseMessage));

		const deltas$: Observable<AgentConfigurationTextDeltaMessage> = this.webSocketService
			.onMessageType(RebasedWebSocketMessageType.aiAgentConfigurationTextDelta)
			.pipe(map((msg: RebasedWebSocketMessage) => msg as AgentConfigurationTextDeltaMessage));

		const done$: Observable<TmsAgentConfigResponseMessage> = lifecycle$.pipe(
			filter((msg: TmsAgentConfigResponseMessage) => msg.payload.status === 'success' || msg.payload.status === 'failed'),
			take(1),
			shareReplay({ bufferSize: 1, refCount: false })
		);

		const processingEvents$: Observable<AgentStreamEvent> = lifecycle$.pipe(
			filter((msg: TmsAgentConfigResponseMessage) => msg.payload.status === 'processing'),
			takeUntil(done$),
			map((): AgentStreamEvent => ({ type: 'processing' }))
		);

		const deltaEvents$: Observable<AgentStreamEvent> = deltas$.pipe(
			takeUntil(done$),
			map(
				(msg: AgentConfigurationTextDeltaMessage): AgentStreamEvent => ({
					type: 'delta',
					content: msg.payload.delta,
				})
			)
		);

		const completionEvent$: Observable<AgentStreamEvent> = done$.pipe(
			map(
				(msg: TmsAgentConfigResponseMessage): AgentStreamEvent =>
					msg.payload.status === 'success'
						? { type: 'complete', agentConfig: msg.payload.agentConfig ?? null, preview: msg.payload.preview ?? null }
						: { type: 'error', message: msg.payload.message ?? 'Stream error' }
			)
		);

		return merge(processingEvents$, concat(deltaEvents$, completionEvent$)).pipe(
			takeWhile((event: AgentStreamEvent) => event.type !== 'complete' && event.type !== 'error', true),
			timeout(timeoutConfig),
			takeUntil(this.abort$)
		);
	}
}
